import React, { useEffect, useState } from "react";
import { Button, Col, Empty, message, Row, Space, Tag, Typography } from "antd";
import { ArrowLeftOutlined, SaveOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { routes } from "common/routes";
import {
  clearConfigDetail,
  getPlatformConfigDetail,
  getPlatformConfigs,
  selectPlatformConfigState,
  updatePlatformConfig,
} from "store/platform-config/platformConfigSlice";
import { fetchInitConfig } from "store/app/appSlice";
import ConfigEntriesEditor from "./components/ConfigEntriesEditor";
import {
  configFromEntries,
  DEFAULT_VERSION,
  entriesFromConfig,
  IConfigEntry,
} from "./configUtils";

/** Platform whose config powers the logged-in app bootstrap (Source options, etc.). */
const APP_RUNTIME_PLATFORM = "wms-web";

const MESSAGE_KEY = "platform-config-detail";
const { Text } = Typography;

/**
 * Edit page for a single config version. The config id (needed by the update
 * API) comes from the platform listing, which is re-fetched here so deep links
 * work too.
 */
const PlatformConfigDetailPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { name, version } = useParams<{ name: string; version: string }>();

  const { detail, detailStatus, detailError, configs, platform, saveStatus } =
    useAppSelector(selectPlatformConfigState);

  const [entries, setEntries] = useState<IConfigEntry[]>([]);
  const [isDirty, setIsDirty] = useState(false);

  const configId = configs.find(
    (item) => item.name === name && item.version === version
  )?.id;

  useEffect(() => {
    if (!name || !version) return;
    dispatch(getPlatformConfigDetail({ name, version }));
    // Listing is the source of the config id — refresh it when deep-linked.
    if (platform !== name) {
      dispatch(getPlatformConfigs(name));
    }
    return () => {
      dispatch(clearConfigDetail());
    };
  }, [name, version, dispatch]);

  // Seed the editor whenever a fresh detail arrives.
  useEffect(() => {
    if (detail) {
      setEntries(entriesFromConfig(detail.config ?? {}));
      setIsDirty(false);
    }
  }, [detail]);

  useEffect(() => {
    if (detailStatus === "loading") {
      message.loading({ content: "Loading config...", key: MESSAGE_KEY });
    } else if (detailStatus === "error") {
      message.error({ content: detailError, key: MESSAGE_KEY, duration: 3 });
    } else {
      message.destroy(MESSAGE_KEY);
    }
    return () => message.destroy(MESSAGE_KEY);
  }, [detailStatus, detailError]);

  const goBack = () =>
    navigate(`/${routes.PLATFORM_CONFIGS.ALL}?platform=${name}`);

  const handleEntriesChange = (updated: IConfigEntry[]) => {
    setEntries(updated);
    setIsDirty(true);
  };

  const handleSave = async () => {
    if (!name || !version) return;
    if (configId === undefined) {
      message.error("Could not resolve the config id. Please go back and retry.");
      return;
    }
    const { config, error } = configFromEntries(entries);
    if (error || !config) {
      message.error(error);
      return;
    }
    try {
      await dispatch(
        updatePlatformConfig({ id: configId, name, version, config })
      ).unwrap();
      message.success("Config updated successfully");
      setIsDirty(false);
      // Keep in-app bootstrap config (Source dropdown, QR validity, etc.) in sync
      // when the active wms-web default config is edited from this panel.
      // config/get/wms-web resolves the default — other versions do not affect runtime.
      if (name === APP_RUNTIME_PLATFORM && version === DEFAULT_VERSION) {
        dispatch(fetchInitConfig());
      }
    } catch (err: any) {
      message.error(err?.message || "Failed to update config");
    }
  };

  const isSaving = saveStatus === "saving";

  return (
    <>
      <Row justify="space-between" align="middle">
        <Col>
          <Space align="center">
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={goBack} />
            <AppPageHeader title={`Edit Config — ${name}`} />
            {version === DEFAULT_VERSION ? (
              <Tag color="gold">DEFAULT</Tag>
            ) : (
              <Tag color="blue">v{version}</Tag>
            )}
          </Space>
        </Col>
        <Col>
          <Space>
            {isDirty && <Text type="warning">Unsaved changes</Text>}
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSave}
              loading={isSaving}
              disabled={!isDirty || detailStatus !== "success"}
            >
              Save Config
            </Button>
          </Space>
        </Col>
      </Row>

      {version === DEFAULT_VERSION && (
        <Row className="mb-4">
          <Col>
            <Text type="secondary">
              The default config applies to all versions of this platform unless a
              version overrides it.
            </Text>
          </Col>
        </Row>
      )}

      <Row className="mt-4">
        <Col xs={24}>
          {detailStatus === "error" ? (
            <Row justify="center" align="middle" style={{ minHeight: 300 }}>
              <Col>
                <Empty description={detailError || "Failed to load config"}>
                  <Button onClick={goBack}>Back to Configs</Button>
                </Empty>
              </Col>
            </Row>
          ) : (
            <ConfigEntriesEditor
              entries={entries}
              onChange={handleEntriesChange}
              disabled={detailStatus !== "success" || isSaving}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default PlatformConfigDetailPage;
